import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { makeStyles } from '@mui/styles';
import { AppContext } from '../../../../RoutesWithAuth';
import axios from '../../../common/AxiosConfig';
import CustomGrid from '../customgrid';
import EditDialog from '../customgrid/EditDialog';
import { IconButton, Tooltip } from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { LoadingIndicator } from '../reports/reconcilationreport/MuiStyled';
import DeleteDialog from '../admin/settings/DeleteDialog';
import FloatingMessage from '../../../common/FloatingMessage';
import PermissionButton from '../PermissionButton';
import { RESOURCE_PERMISSIONS, useRolePermissions } from '../../../common/RolePermissions';

const useStyles = makeStyles(() => ({
    wrapper: {
        height: 'calc(100% - 50px)',
        position: 'relative',
    },
    toolbar: {
        display: 'flex',
        gap: '8px',
        marginBottom: '8px',
        alignItems: 'center',
    },
}));

const EDIT_FIELDS = [
    { name: 'parameter', label: 'Parameter', type: 'text', required: true },
    { name: 'value', label: 'Value', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'text', required: true },
];

function Limits() {
    const classes = useStyles();
    const context = useContext(AppContext);
    const authToken = context.authToken.get;
    const isAdmin = context.isAdmin.get;
    const { hasPermission } = useRolePermissions();
    const canAddLimit = hasPermission(RESOURCE_PERMISSIONS.ADD_LIMIT);

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [isNewRecord, setIsNewRecord] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteRecord, setDeleteRecord] = useState(null);
    const [error, setError] = useState('');
    const [messageType, setMessageType] = useState('error');

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken]);

    const fetchData = useCallback(() => {
        setIsLoading(true);
        axios.get('/admin/limits', {
            headers: { Authorization: authToken }
        }).then(res => {
            setIsLoading(false);
            const items = Array.isArray(res.data) ? res.data : [];
            // Sort alphabetically by parameter
            items.sort((a, b) => (a.parameter || '').localeCompare(b.parameter || ''));
            setData(items);
        }).catch(err => {
            setIsLoading(false);
            console.error('Failed to fetch limits:', err);
        });
    }, [authToken]);

    const handleEditClick = useCallback((row) => {
        setEditingRow(row);
        setIsNewRecord(false);
        setEditDialogOpen(true);
    }, []);

    const handleAddClick = useCallback(() => {
        if (!canAddLimit) return;
        setEditingRow({ parameter: '', value: '', description: '' });
        setIsNewRecord(true);
        setEditDialogOpen(true);
    }, [canAddLimit]);

    const handleEditClose = useCallback(() => {
        setEditDialogOpen(false);
        setEditingRow(null);
    }, []);

    const handleEditSave = useCallback((formValues) => {
        if (isNewRecord && !canAddLimit) return;
        const payload = {
            parameter: formValues.parameter.trim(),
            value: formValues.value.trim(),
            description: formValues.description.trim(),
        };

        const request = isNewRecord
            ? axios.post('/admin/limits', payload, { headers: { Authorization: authToken } })
            : axios.put(`/admin/limits/${encodeURIComponent(editingRow.id)}`, payload, { headers: { Authorization: authToken } });

        request.then(() => {
            fetchData();
            setError(isNewRecord ? 'Limit added successfully' : 'Limit updated successfully');
            setMessageType('success');
        }).catch(err => {
            const msg = err.response?.data?.message || 'An error occurred. Please try again.';
            setError(msg);
            setMessageType('error');
        }).finally(() => {
            setEditDialogOpen(false);
            setEditingRow(null);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, isNewRecord, editingRow, canAddLimit]);

    const handleDeleteClick = useCallback((row) => {
        setDeleteRecord(row);
        setOpenDeleteDialog(true);
    }, []);

    const handleDeleteDialogClose = useCallback(() => {
        setDeleteRecord(null);
        setOpenDeleteDialog(false);
    }, []);

    const handleDeleteDialogSubmit = useCallback(() => {
        if (!deleteRecord) return;
        axios.delete(`/admin/limits/${encodeURIComponent(deleteRecord.id)}`, {
            headers: { Authorization: authToken }
        }).then(() => {
            fetchData();
            setError('Limit deleted successfully');
            setMessageType('success');
        }).catch(err => {
            const msg = err.response?.data?.message || 'Failed to delete limit.';
            setError(msg);
            setMessageType('error');
        }).finally(() => {
            setDeleteRecord(null);
            setOpenDeleteDialog(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, deleteRecord]);

    const columns = useMemo(() => {
        const cols = [
            { name: '#', mapping: 'rownum', width: 50, sortable: false, disableColumnMenu: true, hideable: false },
            { name: 'Parameter', mapping: 'parameter', width: 220, sortable: true },
            { name: 'Value', mapping: 'value', width: 120, sortable: false },
            { name: 'Description', mapping: 'description', sortable: false, displayTootip: ['description'] },
        ];
        cols.push({
            name: 'Edit', mapping: 'actions_edit', width: 70, sortable: false, hideable: false, disableColumnMenu: true,
            renderCell: ({ row }) => (
                <PermissionButton permission={RESOURCE_PERMISSIONS.EDIT_LIMIT} iconOnly size="small" onClick={(e) => { e.stopPropagation(); handleEditClick(row); }}>
                    <Edit fontSize="small" />
                </PermissionButton>
            )
        });
        cols.push({
            name: 'Delete', mapping: 'actions_delete', width: 80, sortable: false, hideable: false, disableColumnMenu: true,
            renderCell: ({ row }) => (
                <PermissionButton permission={RESOURCE_PERMISSIONS.DELETE_LIMIT} iconOnly size="small" onClick={(e) => { e.stopPropagation(); handleDeleteClick(row); }}>
                    <Delete fontSize="small" />
                </PermissionButton>
            )
        });

        return cols;
    }, [isAdmin, handleEditClick, handleDeleteClick]);

    const rows = useMemo(() =>
        data.map((row, i) => ({ ...row, rownum: i + 1 })),
        [data]);

    const editFields = useMemo(() => {
        if (isNewRecord) return EDIT_FIELDS;
        // Parameter is not editable when updating
        return EDIT_FIELDS.map(f => f.name === 'parameter' ? { ...f, disabled: true } : f);
    }, [isNewRecord]);

    return (
        <div className={classes.wrapper}>
            {isLoading && <LoadingIndicator />}
            <FloatingMessage open={error !== ''} message={error} severity={messageType} onClose={() => setError('')} />

            <div className={classes.toolbar}>
                <PermissionButton permission={RESOURCE_PERMISSIONS.ADD_LIMIT} variant="outlined" size="small" startIcon={<Add />} onClick={handleAddClick}>
                    Add Limit
                </PermissionButton>
            </div>

            <CustomGrid
                columns={columns}
                rows={rows}
                customHeight="100%"
            />

            <EditDialog
                open={editDialogOpen}
                onClose={handleEditClose}
                onSave={handleEditSave}
                row={editingRow}
                fields={editFields}
                title={isNewRecord ? 'Add Limit' : 'Edit Limit'}
            />

            <DeleteDialog
                openDeleteDialog={openDeleteDialog}
                title="Delete Limit Confirmation"
                handleDeleteDialogClose={handleDeleteDialogClose}
                handleDeleteDialogSubmit={handleDeleteDialogSubmit}
            >
                Are you sure you want to delete the following limit:
                <br />
                <div className="deleteDialogKey">
                    {deleteRecord?.parameter}
                </div>
            </DeleteDialog>
        </div>
    );
}

export default Limits;
